import type { PaletteMode, Theme } from '@mui/material/styles';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { createContentTheme } from '@/theme';

/* ─── localStorage key ──────────────────────────────────────────── */
const LS_KEY = 'invyo_theme_mode';

function getStoredMode(): PaletteMode {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw === 'light' || raw === 'dark') return raw;
  } catch {
    // localStorage unavailable
  }
  return 'dark';
}

/* ─── Context ───────────────────────────────────────────────────── */
interface ThemeContextValue {
  mode: PaletteMode;
  toggleMode: () => void;
  /** MUI theme for the content area (follows user preference). */
  contentTheme: Theme;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/* ─── Provider ──────────────────────────────────────────────────── */
export function InvyoThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<PaletteMode>(getStoredMode);

  const toggleMode = useCallback(() => {
    setMode((prev) => {
      const next: PaletteMode = prev === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(LS_KEY, next);
      } catch {
        // silently ignore
      }
      return next;
    });
  }, []);

  const contentTheme = useMemo(() => createContentTheme(mode), [mode]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, toggleMode, contentTheme }),
    [mode, toggleMode, contentTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/* ─── Hook ──────────────────────────────────────────────────────── */
// eslint-disable-next-line react-refresh/only-export-components
export function useThemeMode() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeMode must be used inside InvyoThemeProvider');
  return ctx;
}
